/**
 * The POLICY gate rule — which agreement, if any, an owner must deal with before continuing.
 *
 * Lives in its OWN module, free of React and of `import.meta.env`, for two reasons. It is a
 * pure rule and should be reasoned about (and tested) without a renderer; and `use-persona.ts`
 * reaches `auth-context.tsx` and `api/me.ts` through `useMe`, which drags JSX and Vite's
 * `import.meta.env` into any tsconfig that imports it — `typecheck:tests` fails on both.
 *
 * ONE function covering BOTH agreements, deliberately, rather than a gate each: the app shell
 * has to pick a single destination, and two independent gates racing to redirect is the drift
 * class this file exists to prevent (CLAUDE.md rule 5 — a rule in two places is half a rule in
 * each).
 *
 * PRECEDENCE: Terms before DPA. The Terms are the Principal Agreement and the DPA is "part of,
 * and subject to" them (ToS clause 1.2), so accepting the DPA first would be signing a child
 * contract while unbound by its parent.
 *
 * Two distinct states, deliberately asymmetric:
 *
 *   block  - the owner must accept before doing anything else.
 *   notice - superseded DPA terms, notice period still running. Nudge only; nothing is
 *            withheld. DPA ONLY — see below.
 *
 * The ToS half has NO notice state. DPA clause 16.2 promises a notice window and so earns one;
 * ToS clause 11 promises only that we publish a new version and ask you to accept it, so a
 * stale ToS blocks on the same terms as a never-accepted one. Adding a grace window here would
 * be a commitment the contract does not make.
 *
 * Only OWNERS are ever gated: they are the sole signatory for both (`practice.tos_accept` and
 * `practice.dpa_accept` are owner-only server-side), so gating anyone else would trap a user in
 * front of a button they cannot press. Staff keep working throughout.
 */

export type PolicyStatus = 'never' | 'stale' | 'current';

/**
 * The membership fields this rule reads — declared structurally rather than importing
 * `PracticeMembership`, so the module stays free of the api/me.ts dependency chain.
 * `PracticeMembership` satisfies this by shape; a field renamed there is a compile error at
 * the call site in use-persona.ts, which is where it should be.
 */
export type PolicyGateMembership = {
  readonly practiceId: string;
  readonly roleInPractice: string;
  readonly dpaStatus: PolicyStatus;
  readonly dpaReacceptDueAt: number | null;
  readonly tosStatus: PolicyStatus;
};

export type PolicyKind = 'terms' | 'dpa';

export interface PolicyGate {
  /** Practice the owner must act on before continuing, or null. */
  readonly blockPracticeId: string | null;
  /** Which agreement is blocking — selects the route to send them to. */
  readonly blockPolicy: PolicyKind | null;
  /** Practice with updated DPA terms still inside its notice period, or null. */
  readonly noticePracticeId: string | null;
  /** Deadline for the notice-period practice (epoch ms). */
  readonly noticeDueAt: number | null;
  /** The blocked practice owes BOTH, its first acceptance: one agreements page asks for them. */
  readonly bothOwed: boolean;
}

/** The policy gate's own pages, by URL tail under /practices/:id/. */
export const POLICY_GATE_TAILS = ['terms', 'dpa'] as const;
/**
 * The way back for a plan-only practice: its plan page and the policy gate's pages. ONE home;
 * the shell's interstitial, the nav context and the server's PLAN_ONLY_ROUTES (pinned by
 * tests/architecture/way-back-single-home.test.ts) all derive from here.
 */
export const WAY_BACK_TAILS = ['plan', ...POLICY_GATE_TAILS] as const;
/** The export screen — the way OUT, not a way back. DPA 14.1/ToS 10.4 promise it stays
 *  reachable while cancelled or suspended; the shell hid it for exactly those statuses. */
export const EXPORT_TAIL = 'compliance';
export const REACHABLE_WHILE_RESTRICTED = [...WAY_BACK_TAILS, EXPORT_TAIL] as const;
const matchesTail = (pathname: string, tail: string): boolean =>
  new RegExp(`^/practices/[^/]+/${tail}(?:/|$)`).test(pathname);
/* No isWayBackPath: unused once the shell took the wider list, and exporting both is a footgun. */
export function isReachableWhileRestricted(pathname: string): boolean {
  return REACHABLE_WHILE_RESTRICTED.some((t) => matchesTail(pathname, t));
}

export function derivePolicyGate(
  memberships: readonly PolicyGateMembership[],
  now: number,
): PolicyGate {
  // not-authorization: onboarding policy gate — which practices need the owner to act.
  const owned = memberships.filter((m) => m.roleInPractice === 'owner');

  // Terms first — the parent contract. Never-accepted and stale are treated alike.
  const termsBlock = owned.find((m) => m.tosStatus !== 'current');
  if (termsBlock !== undefined) {
    return {
      blockPracticeId: termsBlock.practiceId,
      blockPolicy: 'terms',
      noticePracticeId: null,
      noticeDueAt: null,
      bothOwed: termsBlock.tosStatus === 'never' && termsBlock.dpaStatus === 'never',
    };
  }

  // Never-signed outranks stale: it is the onboarding step, and the practice is
  // unusable until it completes.
  const never = owned.find((m) => m.dpaStatus === 'never');
  const stale = owned.filter((m) => m.dpaStatus === 'stale');
  const overdue = stale.find((m) => m.dpaReacceptDueAt !== null && m.dpaReacceptDueAt <= now);
  const pending = stale.find((m) => m.dpaReacceptDueAt === null || m.dpaReacceptDueAt > now);

  const block = never ?? overdue ?? null;
  return {
    blockPracticeId: block?.practiceId ?? null,
    blockPolicy: block === null ? null : 'dpa',
    noticePracticeId: block === null ? (pending?.practiceId ?? null) : null,
    noticeDueAt: block === null ? (pending?.dpaReacceptDueAt ?? null) : null,
    bothOwed: false,
  };
}

/** A new practice's ONE page for both agreements and the owner's undertaking (owner 2026-10-27).
 *  Not a POLICY_GATE_TAIL: it has no server route of its own, only the terms and DPA ones. */
export const AGREEMENTS_TAIL = 'agreements';

export function isPolicyGateRoute(pathname: string): boolean {
  return [...POLICY_GATE_TAILS, AGREEMENTS_TAIL].some((t) => new RegExp(`^/practices/[^/]+/${t}$`).test(pathname));
}

/** The route that clears the block, or null when nothing is blocking. */
export function policyGateRoute(gate: PolicyGate): string | null {
  if (gate.blockPracticeId === null || gate.blockPolicy === null) return null;
  const suffix = gate.bothOwed ? AGREEMENTS_TAIL : gate.blockPolicy === 'terms' ? 'terms' : 'dpa';
  return `/practices/${gate.blockPracticeId}/${suffix}`;
}
